<script lang="ts" setup>
import type { FavoriteCreateInput, FavoriteList } from '~/models/Favorites'
import type { BaseItem } from '~/models/Content/BaseItem'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { faCheck, faPlus } from '@fortawesome/duotone-regular-svg-icons'
import { colors, getColorForId, popThisColor } from '~/utils/colors'
import { AuToast } from '~/stores/toasts'
import useProductStore from '~/stores/product'
import useAuthStore from '~/stores/auth'
import { PendoFeature } from '~/models/Pendo'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import { useFavorites } from '~/composables/useFavorites'
import ScrollBox from '~/components/utils/ScrollBox.vue'
import AuCallout from '~/components/ui/AuCallout.vue'
import FavoriteDialog from '~/components/favorites/dialogs/FavoriteDialog.vue'
import StackedCard from '~/components/cards/StackedCard.vue'

const props = defineProps<{
  item: BaseItem
  isLoading: boolean
}>()

const emit = defineEmits<{
  addToList: [{ item: FavoriteCreateInput, list: FavoriteList }]
}>()

const { t } = useI18n()
const { getProductFromResource } = useProductStore()
const { userPreferredLanguage } = storeToRefs(useAuthStore())
const { buildResourceUrl } = useUrlBuilder()
const { favoriteLists, FAVORITES_LIMIT } = useFavorites()
const { data: lists } = favoriteLists

const isTeacherItem = computed(() => props.item?.access === 'teacher')

const limitReached = (list: FavoriteList) => (list.favorites ?? []).length >= FAVORITES_LIMIT

const addToList = (item: BaseItem, list: FavoriteList) => {
  if (limitReached(list)) {
    AuToast.error(t('favorite.limitReached'))
    return
  }
  return emit('addToList', {
    item: {
      name: item.title,
      hidden: false,
      aunivers: {
        contentType: item.contentTypeIdentifier,
        contentUrl: buildResourceUrl(item),
        contentId: item.contentId,
        locationId: item.locationId,
        language: userPreferredLanguage.value,
        grade: item.grades,
        subject: getProductFromResource(item)?.subjects || [],
        label: item.label ?? '',
        activity: item.activity ?? '',
        genre: item.genre ?? '',
        imageUrl: item.thumbnail,
        imageAltText: item.thumbnailAlt
      },
    },
    list,
  })
}
</script>

<template>
  <div>
    <div class="-m-6 mt-0 pb-10">
      <ScrollBox class="h-[40dvh] px-6">
        <AuCallout
          v-if="isTeacherItem"
          variant="warning"
          class="mb-4"
          :title="t('share.teacher.title')"
        >
          {{ t('share.teacher.list') }}
        </AuCallout>

        <ul class="mb-4 grid grid-cols-[max-content_auto_max-content] gap-4">
          <li
            v-for="list in lists"
            :key="`favorite-list-${list.id}`"
            class="col-span-3 grid grid-cols-subgrid"
          >
            <button
              v-if="list.id"
              class="group col-span-3 -mx-2 grid grid-cols-subgrid rounded-md p-2 transition hover:bg-(--favorite-custom-bg) focus-visible:ring"
              :class="limitReached(list) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'"
              :style="{ '--favorite-custom-bg': popThisColor(getColorForId(list.id, colors)) }"
              :data-pendo="PendoFeature.Favorites.Add"
              :disabled="limitReached(list)"
              @click="addToList(props.item, list)"
            >
              <StackedCard
                class="grid aspect-square size-12"
                :color="getColorForId(list.id, colors)"
                padded-stack
                element="div"
              >
                <div class="self-center text-center">
                  <span
                    :style="`color: ${getColorForId(list.id, colors)}; filter: brightness(40%)`"
                    class="font-black"
                    v-text="list.favorites?.length ?? 0"
                  />
                </div>
              </StackedCard>
              <span
                class="self-center text-start font-semibold"
                v-text="list.name"
              />
              <span
                v-if="limitReached(list)"
                class="mr-2 self-center justify-self-end"
                v-text="t('favorite.limitShort')"
              />
              <font-awesome-icon
                v-else-if="list.favorites?.some((favorite) => Number(favorite?.aunivers?.locationId) === item.locationId)"
                :icon="faCheck"
                class="mr-2 self-center justify-self-end"
              />
              <div
                v-else
                :data-pendo="PendoFeature.Favorites.Add"
                class="invisible mr-2 self-center justify-self-end rounded-md bg-green-50 px-3 py-1 text-white transition-none group-hover:visible"
              >
                {{ t('save') }}
              </div>
            </button>
          </li>
        </ul>
      </ScrollBox>
      <FavoriteDialog
        action="create"
        :item="item"
      >
        <template #activator="{ onActivate }">
          <button class="group bg-seagreen-5 -mb-10 grid w-full cursor-pointer grid-cols-[max-content_minmax(0,1fr)] items-center gap-5 rounded-b-2xl p-6 text-start focus-visible:ring" @click="onActivate">
            <font-awesome-icon
              :icon="faPlus"
              class="group-hover:bg-seagreen-10 justify-self-start rounded-full bg-white p-4 transition"
            />
            <span
              class="text-xl"
              v-text="t('favorite.createListButtonName')"
            />
          </button>
        </template>
      </FavoriteDialog>
    </div>
  </div>
</template>
